"use client"

import { useEffect, useState } from "react"
import { Button } from "@/components/ui/button"
import { EnvelopeIcon } from "@heroicons/react/24/outline"
import { CardContent } from "@/components/ui/card"
import { BlogAndSubscribeDetails } from "@/app/components/SubscribeModal/types"
import { formatSubscriberCount } from "@/lib/utils/text"
import ModalCard from "../components/ModalCard"
import ModalImage from "../components/ModalImage"
import ModalTitle from "../components/ModalTitle"
import ModalSubtitle from "../components/ModalSubtitle"
import FormInputField from "../components/FormInputField"
import { ErrorResponse } from "@types"
import ErrorAlert from "../components/ErrorAlert"
import { validateEmail } from "@/util/format"
import { onComplete } from "./onComplete"
import Loading from "../components/Loading"
import { useRouter } from "next/navigation"
import { emailSubmitted, subscribeError } from "../tracking"
import { getBlogTheme } from "@/lib/utils/themes"
import { isSubscribeModalOpenAtom } from "@/app/contexts/Atoms"
import { useAtom } from "jotai"
import { SpecialSubscribeFooterText } from "../SpecialPublications"
import { useSearchParams } from "next/navigation"
import { getBlogUrl } from "@/lib/utils/blog"

/**
 * A form that allows the user to enter their email address.
 *
 * @param props.blogDetails - The blog that the user is subscribing to.
 * @param props.redirectTo - The URL to redirect to if we're skipping the form.
 * @param props.prefilledEmail - In certain contexts, we may want to prefill the email field and require the user to explicitly click "submit" to subscribe (eg, the full-page subscribe takeover).
 */
export default function EnterEmailForm({
  blogDetails,
  redirectTo,
  prefilledEmail,
  embed,
  children,
}: {
  blogDetails: BlogAndSubscribeDetails
  redirectTo?: string
  prefilledEmail?: string
  embed?: boolean
  children?: React.ReactNode
}) {
  const [email, setEmail] = useState<string>(prefilledEmail || "")
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<ErrorResponse | null>(null)
  const [_, setIsSubscribeModalOpen] = useAtom(isSubscribeModalOpenAtom)

  const router = useRouter()
  const searchParams = useSearchParams()

  const minimal = searchParams?.get("minimal")

  // If this component loads, that means that the user has either clicked the Subscribe button CTA
  // or else this modal page has loaded as the first page in the full page modal.
  // In either case we want to mark the subscribe modal as having been opened.
  useEffect(() => {
    setIsSubscribeModalOpen(true)

    if (redirectTo) {
      // It's crucial to do the redirect from within a useEffect and not directly in the component mount
      // as Next will throw an error that you can't redirect while rendering a component, and if you try
      // subsequent router operations like router.back() (which closes out of the modal) will error out in
      // some circumstances.
      router.replace(redirectTo)
    }
  }, [])

  if (redirectTo) {
    return <Loading />
  }

  const themeClassName = getBlogTheme(blogDetails.theme)

  const formattedSubscriberCount = formatSubscriberCount(
    blogDetails.subscriberCount
  )?.toLowerCase()

  const subscribeMessage =
    blogDetails.subscriberCount > 1000
      ? `Join ${formattedSubscriberCount} and stay updated by getting the latest posts delivered directly to your inbox.`
      : `Stay updated by getting the latest posts delivered directly to your inbox.`

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault()

    if (isLoading) return // Prevent double-submits

    // Reset error.
    setError(null)

    // Email validation.
    if (!email || !validateEmail(email)) {
      const errMsg = "Enter a valid email address."
      setError({ msg: errMsg })

      // Track the error on Amplitude.
      subscribeError(blogDetails?.id || "", errMsg)

      return
    }

    setIsLoading(true)

    try {
      // Subscribe the user and navigate to the next step. If embed, navigate directly to embed success page
      const nextPath = !embed
        ? await onComplete(blogDetails, email)
        : `embed/success${minimal ? "?minimal=true" : ""}`

      emailSubmitted(blogDetails.id, email)

      router.replace(nextPath)
    } catch (error) {
      setIsLoading(false)
      throw error
    }
  }

  // If minimal embed, show minimal subscribe form
  if (minimal === "true") {
    const vertical = searchParams?.get("vertical") === "true"

    return (
      <div className="items-center p-0 sm:mr-16 border-none">
        <EnterEmailFormContents
          handleSubscribe={handleSubscribe}
          email={email}
          setEmail={setEmail}
          error={error}
          prefilledEmail={prefilledEmail}
          isLoading={isLoading}
          minimal={true}
          vertical={vertical}
        />
      </div>
    )
  }

  const blogUrl = getBlogUrl(blogDetails)

  return (
    <ModalCard className={embed ? "sm:mr-16" : undefined}>
      <a href={blogUrl.fullUrl} target="_blank" rel="noopener noreferrer">
        <ModalImage imgSrc={blogDetails.logo_url} />
      </a>
      <ModalTitle>Subscribe to {blogDetails.name}</ModalTitle>

      <CardContent className={themeClassName}>
        <ModalSubtitle>{subscribeMessage}</ModalSubtitle>
        <EnterEmailFormContents
          handleSubscribe={handleSubscribe}
          email={email}
          setEmail={setEmail}
          error={error}
          prefilledEmail={prefilledEmail}
          isLoading={isLoading}
        />
        {children}
        <SpecialSubscribeFooterText blogId={blogDetails.id} />
      </CardContent>
    </ModalCard>
  )
}

const EnterEmailFormContents = ({
  handleSubscribe,
  email,
  setEmail,
  error,
  prefilledEmail,
  isLoading,
  minimal = false,
  vertical = false,
}: {
  handleSubscribe: (e: React.FormEvent) => void
  email: string
  setEmail: (value: string) => void
  error: ErrorResponse | null
  prefilledEmail?: string
  isLoading: boolean
  minimal?: boolean
  vertical?: boolean
}) => {
  // For minimal mode, use different styling based on vertical parameter.
  if (minimal) {
    // Vertical layout for minimal mode.
    if (vertical) {
      return (
        <form onSubmit={handleSubscribe} className="space-y-2" noValidate>
          {error && <ErrorAlert>{error.msg}</ErrorAlert>}
          <FormInputField
            value={email}
            onChange={(value) => setEmail(value)}
            type="email"
            placeholder="you@youremail.com"
            icon={EnvelopeIcon}
            disabled={!!prefilledEmail}
          />
          <Button
            type="submit"
            variant="default"
            className="w-full"
            disabled={isLoading}
          >
            {isLoading ? "..." : "Subscribe"}
          </Button>
        </form>
      )
    }

    // Horizontal layout for minimal mode (default).
    return (
      <form
        onSubmit={handleSubscribe}
        className="flex flex-row items-center space-y-0 space-x-2"
        noValidate
        style={{ margin: 0, padding: 0 }}
      >
        {error && <ErrorAlert>{error.msg}</ErrorAlert>}
        <div className="flex-grow">
          <FormInputField
            value={email}
            onChange={(value) => setEmail(value)}
            type="email"
            placeholder="you@youremail.com"
            icon={EnvelopeIcon}
            disabled={!!prefilledEmail}
          />
        </div>
        <Button
          type="submit"
          variant="default"
          size="sm"
          className="whitespace-nowrap"
          disabled={isLoading}
        >
          {isLoading ? "..." : "Subscribe"}
        </Button>
      </form>
    )
  }

  // Standard layout
  return (
    <form onSubmit={handleSubscribe} className="space-y-4" noValidate>
      {error && <ErrorAlert>{error.msg}</ErrorAlert>}
      <FormInputField
        value={email}
        onChange={(value) => setEmail(value)}
        type="email"
        placeholder="you@youremail.com"
        icon={EnvelopeIcon}
        disabled={!!prefilledEmail}
      />
      <Button
        type="submit"
        variant="default"
        className="w-full"
        disabled={isLoading}
      >
        {isLoading ? (
          "Subscribing..."
        ) : (
          <div className="flex items-center gap-2">Subscribe</div>
        )}
      </Button>
    </form>
  )
}
